/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosResponse, AxiosError } from 'axios';
import { Suggestion, Nudge } from 'types/common';
import { datadogLogs } from '@datadog/browser-logs';

// Define API response interfaces
export interface ApiResponse<T> {
    message: string;
    data: T[];
}

/**
 * The LiveKit recorder token this page was launched with. lms-backend accepts
 * it, scoped to its own class, on the reads the overlay panels make (see
 * lms-backend recorder-panels) — so no backend credential ships in this bundle.
 */
const recorderToken = (): string | null =>
    new URLSearchParams(window.location.search).get('livekitToken');

// Create a configured axios instance
const apiService = axios.create({
    baseURL: import.meta.env.VITE_BACKEND_URL || '',
    headers: {
        'Content-Type': 'application/json',
    },
});

apiService.interceptors.request.use(
    (config) => {
        // A caller-supplied Authorization (e.g. the auto-end stop) wins.
        const token = recorderToken();
        if (token && config.headers && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        datadogLogs.logger.error('API Request Error', {
            label: 'api-failure',
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
        }, error as Error);
        return Promise.reject(error);
    }
);

// Add response interceptor to log responses
apiService.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error: AxiosError) => {
        datadogLogs.logger.error('API Response Error', {
            label: 'api-failure',
            url: error.config?.url,
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            data: error?.response?.data,
        }, error as Error);

        return Promise.reject(error);
    }
);

// API methods
export const api = {
    /**
     * Get student names spoken data for a class
     * Returns data about which student names have been spoken by the tutor
     */
    getStudentNamesSpoken: async (classId: number | string): Promise<any | null> => {
        try {
            // Add timestamp to prevent caching
            const timestamp = new Date().getTime();
            
            const response = await apiService.get(
                `/api/v2/in-class/student-names-spoken/${classId}?_t=${timestamp}`
            );
            
            // Handle the nested data structure from the API
            let responseData = {};
            
            if (response.data && response.data.data && response.data.data.data) {
                // The API returns a nested structure with data.data.data
                responseData = response.data.data.data;
            } else if (response.data && response.data.data) {
                // The API returns a structure with data.data
                responseData = response.data.data;
            } else if (response.data) {
                // The API returns data directly
                responseData = response.data;
            }
            
            return responseData;
        } catch (error: unknown) {
            datadogLogs.logger.error('Error fetching student names spoken data', {
                label: 'api-failure',
                api: 'getStudentNamesSpoken',
                statusCode: (error as any)?.response?.status || 'unknown'
            }, error as Error);
            
            return null;
        }
    },

    /**
     * Get suggestions for a class (legacy method - kept for backward compatibility)
     * Returns the full response object with { message, data } structure
     */
    getSuggestions: async (classId: number | string): Promise<ApiResponse<Suggestion> | null> => {
        try {
            const response = await apiService.get<ApiResponse<Suggestion>>(`/api/v2/tutor-suggestions/${classId}`);
            return response.data;
        } catch (error: unknown) {
            datadogLogs.logger.error('Error fetching suggestions', {
                label: 'api-failure',
                api: 'getSuggestions',
            }, error as Error);

            // Return null on error to be handled by the caller
            return null;
        }
    },

    /**
     * Get nudges for a class
     * Returns the full response object with { message, data } structure
     */
    getNudges: async (classId: number | string): Promise<ApiResponse<Nudge> | null> => {
        try {
            const response = await apiService.get<ApiResponse<Nudge>>(`/api/v2/nudge/get-message/${classId}`);
            return response.data;
        } catch (error: unknown) {
            datadogLogs.logger.error('Error fetching nudges', {
                label: 'api-failure',
                api: 'getNudges',
            }, error as Error);
            // Return null on error to be handled by the caller
            return null;
        }
    },

    /**
     * Get student interaction metrics for a class
     * Returns participation data for students in 15-minute windows
     */
    getStudentInteraction: async (classId: number | string): Promise<any | null> => {
        try {
            // Add timestamp to prevent caching
            const timestamp = new Date().getTime();
            
            const response = await apiService.get(
                `/api/v2/in-class/student-interaction/${classId}?_t=${timestamp}`
            );
            
            // Handle the nested data structure from the API
            if (response.data && response.data.data && response.data.data.data) {
                // The API returns a nested structure with data.data.data
                return response.data.data;
            } else if (response.data && response.data.data) {
                // The API returns a structure with data.data
                return response.data;
            } else if (response.data) {
                // The API returns data directly
                return { data: response.data };
            }
            
            return null;
        } catch (error: unknown) {
            datadogLogs.logger.error('Error fetching student interaction data', {
                label: 'api-failure',
                api: 'getStudentInteraction',
                statusCode: (error as any)?.response?.status || 'unknown'
            }, error as Error);
            // Return null on error to be handled by the caller
            return null;
        }
    },
    
    /**
     * Get teacher pace meter data for a class
     * Returns pace metrics for the teacher's speaking rate
     */
    getTeacherPaceMeter: async (classId: number | string): Promise<any | null> => {
        try {
            // Add timestamp to prevent caching
            const timestamp = new Date().getTime();
            
            const response = await apiService.get(
                `/api/v2/in-class/teacher-pace-meter/${classId}?_t=${timestamp}`
            );
            
            // Handle the nested data structure from the API
            if (response.data && response.data.data && response.data.data.data) {
                // The API returns a nested structure with data.data.data
                return response.data.data;
            } else if (response.data && response.data.data) {
                // The API returns a structure with data.data
                return response.data;
            } else if (response.data) {
                // The API returns data directly
                return { data: response.data };
            }
            
            return null;
        } catch (error: unknown) {
            datadogLogs.logger.error('Error fetching teacher pace meter data', {
                label: 'api-failure',
                api: 'getTeacherPaceMeter',
                statusCode: (error as any)?.response?.status || 'unknown'
            }, error as Error);
            // Return null on error to be handled by the caller
            return null;
        }
    },
};

export default apiService;